import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import type { Clip, Player } from "@mdj/shared";
import { api } from "../lib/api";
import { Button, GlassCard } from "../components/Glass";
import { Announcer } from "../components/Announcer";

export function PlayerStep({ clip, initialPlayerId = null, onAssigned, onBack }: { clip: Clip; initialPlayerId?: string | null; onAssigned: (p: Player) => void; onBack: () => void }) {
  const [players, setPlayers] = useState<Player[] | null>(null);
  const [selected, setSelected] = useState<string | null>(initialPlayerId);
  // Launched from the lineup: show only the chosen player until asked otherwise.
  const [showAll, setShowAll] = useState(!initialPlayerId);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState(""); const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { api.listPlayers().then(setPlayers).catch((e: Error) => setErr(e.message)); }, []);

  const addPlayer = async () => {
    if (!name.trim()) return;
    setBusy(true); setErr(null);
    try {
      const p = await api.createPlayer({ name: name.trim(), number: number.trim() || null });
      setPlayers((ps) => [...(ps ?? []), p]); setSelected(p.id); setAdding(false); setName(""); setNumber("");
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const assign = async () => {
    if (!selected) return;
    setBusy(true); setErr(null);
    try {
      onAssigned(await api.assignWalkup(selected, clip.id));
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const sel = players?.find((p) => p.id === selected);

  return (
    <GlassCard key="player">
      <h2>Who walks up?</h2>
      <p className="hint">{initialPlayerId ? "Confirm the player (or pick another), then assign." : "Tap a player, then assign the walk-up."}</p>

      <div className="players" role="listbox" aria-label="Players">
        {players === null && <p className="hint">Loading roster…</p>}
        {players?.filter((p) => showAll || p.id === selected).map((p) => (
          <motion.button
            key={p.id} type="button" role="option" aria-selected={p.id === selected}
            className={`player${p.id === selected ? " on" : ""}`} onClick={() => setSelected(p.id)}
            whileTap={{ scale: 0.98 }}
          >
            <span className="num">{p.number ?? "•"}</span>
            <span className="name">{p.name}</span>
            <span className="meta">{p.walkupClipId ? "🎵" : ""}{p.announcementKey ? " 📣" : ""}</span>
          </motion.button>
        ))}
        {!showAll && <Button ghost block onClick={() => setShowAll(true)}>Pick a different player</Button>}
        <AnimatePresence initial={false}>
          {showAll && adding ? (
            <motion.div key="add" className="row" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ overflow: "hidden" }}>
              <label className="field" style={{ flex: 3 }}><input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus aria-label="Player name" /></label>
              <label className="field" style={{ flex: 1 }}><input placeholder="#" inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value)} aria-label="Number" /></label>
              <Button primary disabled={busy || !name.trim()} onClick={addPlayer} style={{ flex: 1 }}>Add</Button>
            </motion.div>
          ) : showAll ? (
            <motion.div key="addbtn"><Button ghost block onClick={() => setAdding(true)}>+ Add player</Button></motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {sel && (
          <Announcer key={sel.id} player={sel} onSaved={(p) => setPlayers((ps) => (ps ?? []).map((x) => (x.id === p.id ? p : x)))} />
        )}
      </AnimatePresence>

      {err && <p style={{ margin: 0, color: "var(--danger)", fontSize: 13 }}>{err}</p>}
      <div className="row">
        <Button ghost onClick={onBack}>Back</Button>
        <Button primary disabled={!selected || busy} onClick={assign}>{busy ? "Saving…" : "Assign walk-up"}</Button>
      </div>
    </GlassCard>
  );
}
